import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Send, MessageCircleQuestion } from 'lucide-react';
import { capitalize } from './constants';
import { useVereadorChat } from './hooks';
import { VereadorLine, PlayerLine, VereadorTyping } from './ChatBubbles';

export default function MeetVereadorScreen({ vereador, selectedCity, playerAvatar, onContinue, onBack }) {
  const [question, setQuestion] = useState('');
  const scrollRef = useRef(null);
  const cityName = selectedCity?.name || 'sua cidade';
  const { messages, error, ask } = useVereadorChat(vereador, cityName);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    ask(question);
    setQuestion('');
  };

  return (
    <div className="dark rounded-xl border border-[var(--border)] bg-[#1a1a2e] overflow-hidden min-h-[calc(100vh-120px)] sm:min-h-[600px] flex flex-col">
      <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-white/[.06]">
        <div className="flex items-center gap-2 mb-1">
          <MessageCircleQuestion size={20} className="text-[var(--accent)] sm:w-6 sm:h-6" />
          <h2 className="text-lg sm:text-xl font-bold text-white">Primeiro Encontro</h2>
        </div>
        <p className="text-xs sm:text-sm text-gray-400">
          Conheça {capitalize(vereador.nome)} antes de assumir o gabinete
        </p>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6 space-y-3" style={{ scrollbarWidth: 'thin' }}>
        <VereadorLine vereador={vereador}>
          Bem-vindo(a) ao gabinete! Sou {capitalize(vereador.nome)}, vereador(a) pelo {vereador.partido} aqui em {cityName}.
        </VereadorLine>
        <VereadorLine vereador={vereador}>
          Preciso de alguém de confiança para cuidar da minha imagem, arrecadar recursos e, claro, ficar de olho no risco de escândalo.
        </VereadorLine>
        <PlayerLine playerAvatar={playerAvatar}>
          Pode contar comigo. Vamos trabalhar juntos.
        </PlayerLine>
        <VereadorLine vereador={vereador}>
          Ótimo. Antes de começarmos, fique à vontade para me perguntar qualquer coisa sobre o mandato, a cidade ou como vamos trabalhar.
        </VereadorLine>

        {messages.map((m, i) => (
          <React.Fragment key={i}>
            <PlayerLine playerAvatar={playerAvatar}>{m.question}</PlayerLine>
            {m.loading ? <VereadorTyping vereador={vereador} /> : <VereadorLine vereador={vereador}>{m.answer}</VereadorLine>}
          </React.Fragment>
        ))}
      </div>

      <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-white/[.06] space-y-2">
        {error && <p className="text-[11px] text-red-400">{error}</p>}
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={`Pergunte algo a ${capitalize(vereador.nome).split(' ')[0]}...`}
            maxLength={300}
            className="flex-1 py-2.5 px-4 rounded-xl text-sm bg-[#0f1a30] border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-white/30"
          />
          <button
            type="submit"
            disabled={!question.trim() || messages.some((m) => m.loading)}
            className="p-2.5 rounded-xl disabled:opacity-40 transition-opacity"
            style={{ background: 'rgba(212,165,116,.95)', color: '#111' }}
          >
            <Send size={16} />
          </button>
        </form>

        <div className="flex items-center justify-between gap-2 pt-1">
          <button onClick={onBack}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-[10px] sm:text-xs font-medium border border-white/10 text-gray-300 hover:text-white hover:border-white/20 transition-colors shrink-0">
            <ArrowLeft size={12} /> Trocar Vereador
          </button>
          <button
            onClick={onContinue}
            className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-bold transition-all"
            style={{ background: 'rgba(212,165,116,.95)', color: '#111' }}>
            Continuar <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
